import MuiCheckbox, { type CheckboxProps as MuiCheckboxProps } from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import Radio from '@mui/material/Radio';
import MuiRadioGroup from '@mui/material/RadioGroup';
import MuiSwitch, { type SwitchProps as MuiSwitchProps } from '@mui/material/Switch';
import { type ReactElement, type ReactNode, type Ref, useId } from 'react';

import { cssVar } from '../../theme/tokens';
import type { ControlSize } from './sizes';

const LABEL_SX = { mr: 0, gap: 1, '& .MuiFormControlLabel-label': { typography: 'body1' } };

interface ChoiceExtras {
  /** Text next to the control; omit only with an `aria-label` in `slotProps.input`. */
  label?: ReactNode;
  size?: ControlSize;
  /** Help text under the control. */
  hint?: ReactNode;
  /** Error message (marks the input invalid and describes it). */
  error?: ReactNode;
  /** Reaches the `<input>` (React Hook Form `field.ref`, for focus on error). */
  inputRef?: Ref<HTMLInputElement>;
}

/** Label, hint and error around a checkbox or switch, wired with aria-describedby. */
function ChoiceField({
  control,
  label,
  hint,
  error,
  hintId,
  errorId,
}: {
  control: ReactElement;
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  hintId: string;
  errorId: string;
}) {
  const labelled = label ? (
    <FormControlLabel control={control} label={label} sx={LABEL_SX} />
  ) : (
    control
  );
  if (!hint && !error) return labelled;
  return (
    <FormControl error={Boolean(error)}>
      {labelled}
      {hint ? (
        <FormHelperText id={hintId} error={false} sx={{ mx: 0, mt: 0 }}>
          {hint}
        </FormHelperText>
      ) : null}
      {error ? (
        <FormHelperText id={errorId} sx={{ mx: 0, mt: 0 }}>
          {error}
        </FormHelperText>
      ) : null}
    </FormControl>
  );
}

function useChoiceInput(
  hint: ReactNode,
  error: ReactNode,
  inputRef: Ref<HTMLInputElement> | undefined,
  input: object | undefined,
) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy = [error ? errorId : null, hint ? hintId : null].filter(Boolean).join(' ');
  return {
    hintId,
    errorId,
    input: {
      ...input,
      ref: inputRef,
      'aria-invalid': error ? true : undefined,
      'aria-describedby': describedBy || undefined,
    },
  };
}

export interface CheckboxProps extends Omit<MuiCheckboxProps, 'size'>, ChoiceExtras {}

/** A checkbox with its label; checked uses `--control-checked` (navy, navy-200 in dark). */
export function Checkbox({
  label,
  size = 'md',
  hint,
  error,
  inputRef,
  slotProps,
  ...props
}: CheckboxProps) {
  const wiring = useChoiceInput(hint, error, inputRef, slotProps?.input);
  const box = (
    <MuiCheckbox
      size={size === 'sm' ? 'small' : size === 'lg' ? 'large' : 'medium'}
      slotProps={{ ...slotProps, input: wiring.input }}
      {...props}
    />
  );
  return <ChoiceField control={box} label={label} hint={hint} error={error} {...wiring} />;
}

export interface SwitchProps extends Omit<MuiSwitchProps, 'size'>, ChoiceExtras {}

/** An on/off switch with its label. */
export function Switch({
  label,
  size = 'md',
  hint,
  error,
  inputRef,
  slotProps,
  ...props
}: SwitchProps) {
  const wiring = useChoiceInput(hint, error, inputRef, slotProps?.input);
  const control = (
    <MuiSwitch
      size={size === 'sm' ? 'small' : 'medium'}
      slotProps={{ ...slotProps, input: wiring.input }}
      {...props}
    />
  );
  return <ChoiceField control={control} label={label} hint={hint} error={error} {...wiring} />;
}

export interface RadioOption<V extends string = string> {
  value: V;
  label: ReactNode;
  disabled?: boolean;
}

export interface RadioGroupProps<V extends string = string> {
  /** The group's name, shown as the legend. */
  label: ReactNode;
  options: readonly RadioOption<V>[];
  value: V | null;
  onChange: (value: V) => void;
  name?: string;
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  disabled?: boolean;
  /** Options side by side instead of stacked. */
  row?: boolean;
  size?: ControlSize;
}

/** One choice from a few options, as a fieldset with a legend, hint and error. */
export function RadioGroup<V extends string = string>({
  label,
  options,
  value,
  onChange,
  name,
  hint,
  error,
  required = false,
  disabled = false,
  row = false,
  size = 'md',
}: RadioGroupProps<V>) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  return (
    <FormControl
      component="fieldset"
      error={Boolean(error)}
      required={required}
      disabled={disabled}
    >
      <FormLabel
        component="legend"
        sx={{
          mb: 1,
          typography: 'subtitle2',
          color: cssVar('text'),
          '&.Mui-focused': { color: cssVar('text') },
        }}
      >
        {label}
      </FormLabel>
      <MuiRadioGroup
        row={row}
        name={name ?? id}
        value={value ?? ''}
        onChange={(event) => onChange(event.target.value as V)}
        aria-describedby={[errorId, hintId].filter(Boolean).join(' ') || undefined}
      >
        {options.map((option) => (
          <FormControlLabel
            key={option.value}
            value={option.value}
            disabled={option.disabled}
            control={<Radio size={size === 'sm' ? 'small' : 'medium'} />}
            label={option.label}
            sx={{ ...LABEL_SX, mr: row ? 4 : 0 }}
          />
        ))}
      </MuiRadioGroup>
      {hint ? (
        <FormHelperText id={hintId} error={false} sx={{ mx: 0 }}>
          {hint}
        </FormHelperText>
      ) : null}
      {error ? (
        <FormHelperText id={errorId} sx={{ mx: 0 }}>
          {error}
        </FormHelperText>
      ) : null}
    </FormControl>
  );
}
